import React, { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { User, Bell, Key, Palette, Globe } from 'lucide-react'

export const SettingsPage: React.FC = () => {
  const [profile, setProfile] = useState({
    name: 'John Doe',
    email: 'john@expandly.com',
    company: 'Expandly Ltd',
  })

  const [notifications, setNotifications] = useState({
    emailReports: true,
    marketAlerts: true,
    weeklyDigest: false,
  })

  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [language, setLanguage] = useState('en')

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
      <Header />

      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <h1 style={{ fontSize: '32px', marginBottom: '32px' }}>Settings</h1>

        <div style={{ display: 'grid', gap: '24px', maxWidth: '800px' }}>
          {/* Profile Settings */}
          <Card>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={20} /> Profile
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  Company
                </label>
                <input
                  type="text"
                  value={profile.company}
                  onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <Button style={{ alignSelf: 'flex-start' }}>Save Changes</Button>
            </div>
          </Card>

          {/* Notification Settings */}
          <Card>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={20} /> Notifications
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notifications.emailReports}
                  onChange={(e) => setNotifications({ ...notifications, emailReports: e.target.checked })}
                  style={{ marginRight: '12px', width: '18px', height: '18px' }}
                />
                Email me quarterly reports
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notifications.marketAlerts}
                  onChange={(e) => setNotifications({ ...notifications, marketAlerts: e.target.checked })}
                  style={{ marginRight: '12px', width: '18px', height: '18px' }}
                />
                Market opportunity alerts
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notifications.weeklyDigest}
                  onChange={(e) => setNotifications({ ...notifications, weeklyDigest: e.target.checked })}
                  style={{ marginRight: '12px', width: '18px', height: '18px' }}
                />
                Weekly digest
              </label>
            </div>
          </Card>

          {/* Appearance */}
          <Card>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Palette size={20} /> Appearance
            </h3>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                Theme
              </label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
          </Card>

          {/* Language */}
          <Card>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={20} /> Language
            </h3>
            <div>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
          </Card>

          {/* API Keys */}
          <Card>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Key size={20} /> API Keys
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Manage your API keys for programmatic access
            </p>
            <Button variant="outline">Manage API Keys</Button>
          </Card>
        </div>
      </main>
    </div>
  )
}
